import React from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, PlayCircle, Mail, Calendar, RefreshCw, MessageSquare } from "lucide-react";
import Layout, { BookingLink } from "../components/Layout";
import SEO from "../components/SEO";

// Legacy /thank-you (assessment completion) — kept for back-compat
export default function ThankYou() {
  return <ThankYouStateView state="assessment"/>;
}

// Router: /thank-you/:state via useParams
export function ThankYouState() {
  const params = useParams();
  const state = params.state || "assessment";
  return <ThankYouStateView state={state}/>;
}

function ThankYouStateView({ state }) {
  const map = {
    assessment: {
      icon: MessageSquare,
      title: "Scorecard complete",
      body: "Your Fleet Crossroads Scorecard result has been emailed. The next step is a 20-minute conversation with Brandon to connect the score to the decisions inside the business.",
      cta: { href: "booking", label: "Request a 20-Minute Fleet Owner Conversation" },
    },
    application: {
      icon: Mail,
      title: "Your application was received",
      body: "Check your email. Driver Reborn Advisory will review the fleet condition, decision-maker involvement, records, urgency, and readiness before responding with next steps.",
      cta: { to: "/", label: "Return to Home" },
    },
    qualified: {
      icon: Calendar,
      title: "Your application is in review",
      body: "Brandon reviews every application personally. Your situation looks like a fit — pick a time for your 20-minute Fleet Owner Conversation now. The booking link has also been emailed to you.",
      cta: { href: "booking", label: "Book My 20-Minute Conversation" },
    },
    manual_review: {
      icon: Mail,
      title: "Brandon is reviewing your application",
      body: "Your business falls outside the automatic routing rules, so Brandon will review it personally. No promise and no rejection until the situation is understood.",
      cta: { to: "/", label: "Return to Home" },
    },
    growth_track: {
      icon: RefreshCw,
      title: "Strengthen the foundation first",
      body: "Based on what you submitted, the honest next step is to keep building financial visibility, weekly rhythm, and back-office discipline before a custom advisory engagement. Revisit the Fleet Crossroads Scorecard as the business matures.",
      cta: { to: "/fleet-crossroads-scorecard", label: "Revisit the Scorecard" },
    },
    education: {
      icon: PlayCircle,
      title: "Clarity before a custom engagement",
      body: "The honest next step is to strengthen the foundation before a custom advisory engagement. Use your Fleet Crossroads Scorecard result to identify what to work on first.",
      cta: { to: "/fleet-crossroads-scorecard", label: "Take the Fleet Crossroads Scorecard" },
    },
    decline: {
      icon: CheckCircle2,
      title: "Driver Reborn Advisory is not the right fit right now",
      body: "Based on the request described, Driver Reborn Advisory is not the right provider for this situation. We would rather tell you honestly than sell the wrong solution.",
      cta: { to: "/", label: "Return to Home" },
    },
  };
  const v = map[state] || map.assessment;
  const Icon = v.icon;
  return (
    <Layout minimal>
      <SEO title="Thank You | Driver Reborn Advisory" description="Thank you for engaging with Driver Reborn Advisory." noindex={true} />
      <section className="section">
        <div className="container-tight">
          <Icon className="text-[#FF6A00] mb-4" size={40}/>
          <h1 className="font-bebas text-5xl uppercase leading-none mb-4" data-testid={`ty-${state}-title`}>
            {v.title}
          </h1>
          <p className="text-lg text-[#A1A1AA] leading-relaxed mb-8">{v.body}</p>
          {v.cta.href ? (
            <BookingLink testid={`ty-${state}-cta`}>{v.cta.label} <ArrowRight size={20}/></BookingLink>
          ) : (
            <Link to={v.cta.to} className="btn-primary" data-testid={`ty-${state}-cta`}>
              {v.cta.label} <ArrowRight size={20}/>
            </Link>
          )}
        </div>
      </section>
    </Layout>
  );
}

// Legacy named exports still consumed by App.js
export function ThankYouApplication() { return <ThankYouStateView state="application"/>; }
